import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TicketDetail } from "@recursive-staff/schema/contracts";
import { CHANNEL_ICON, CHANNEL_LABEL, TONE, PRIORITY_DOT, type Tone } from "@recursive-staff/ui";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { useScope } from "../app/scope";
import { ErrorNote, Loading } from "./common";

type View = "all" | "mine" | "risk" | "flagged" | "spam" | "deleted";
const VIEWS: View[] = ["all", "mine", "risk", "flagged", "spam", "deleted"];

function waited(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h`;
  return `${Math.floor(minutes / (60 * 24))}d`;
}

const STATUS_TONE: Record<string, Tone> = {
  new: "active",
  open: "ok",
  pending: "warn",
  hold: "bad",
  solved: "neutral",
  closed: "neutral",
};

// Per-message email delivery badge (outbound only). "sent" = accepted by
// Cloudflare; delivered/bounced/etc. are reconciled from CF Email Sending events.
const DELIVERY_BADGE: Record<string, { label: string; color: string }> = {
  sent: { label: "Sent", color: "#8a8e88" },
  delivered: { label: "Delivered", color: "#2f8a57" },
  deferred: { label: "Deferred", color: "#9a7b1f" },
  bounced: { label: "Bounced", color: "#b23b3b" },
  failed: { label: "Failed to send", color: "#b23b3b" },
  rejected: { label: "Rejected", color: "#b23b3b" },
  complained: { label: "Marked as spam", color: "#9a7b1f" },
};

// Per-inbound-message threat banner. Distinct, escalating treatment so a real
// CRITICAL never looks like the everyday "external sender" note (avoids the
// banner-fatigue trap). reasons[].detail is already plain-English.
const RISK_STYLE: Record<string, { bg: string; border: string; fg: string; label: string; icon: string }> = {
  low: { bg: "#f6f5f1", border: "#e7e5df", fg: "#6b706a", label: "Heads up", icon: "circle" },
  medium: { bg: "#fbf1dd", border: "#efe2c0", fg: "#8a5f14", label: "Caution — verify before you act on this", icon: "alert-triangle" },
  high: { bg: "#fbe4e2", border: "#e0857e", fg: "#b3261e", label: "Likely phishing — do not act on this message", icon: "shield-alert" },
  critical: { bg: "#7a1f1a", border: "#7a1f1a", fg: "#ffffff", label: "Dangerous — treat this as an attack", icon: "shield-alert" },
};

const BUBBLE: React.CSSProperties = { fontSize: 13, background: "#fff", border: "1px solid #ececec", color: "#1d241f", borderRadius: 8, lineHeight: 1.55 };

// Safely-rendered inbound email HTML. The sanitized fragment is put in a
// sandboxed, script-less, opaque-origin iframe with a strict inline CSP — that
// sandbox (no allow-scripts / no allow-same-origin / no allow-top-navigation) is
// the load-bearing XSS control. Remote images are already blocked server-side;
// links were rewritten to the click-through interstitial (or made inert on
// high/critical). High/critical mail defaults to plain text with an opt-in.
function EmailBody({ ticketId, messageId, level, plain }: { ticketId: string; messageId: string; level?: string; plain: string | null }) {
  const dangerous = level === "high" || level === "critical";
  const [show, setShow] = useState(!dangerous);
  const body = useQuery({ queryKey: ["msg-body", messageId], queryFn: () => api.messageBody(ticketId, messageId), enabled: show, staleTime: 5 * 60_000 });

  if (!show) {
    return (
      <div>
        <div style={{ ...BUBBLE, padding: "11px 13px", whiteSpace: "pre-wrap" }}>{plain}</div>
        <button onClick={() => setShow(true)} style={{ marginTop: 5, fontSize: 11.5, fontWeight: 600, color: "#b3261e", background: "none", border: 0, cursor: "pointer", padding: 0 }}>
          Show formatted email (links stay disabled)
        </button>
      </div>
    );
  }
  if (body.isLoading) return <div style={{ ...BUBBLE, padding: "11px 13px", color: "#8a8e88" }}>Loading…</div>;
  if (!body.data?.hasHtml || !body.data.html) return <div style={{ ...BUBBLE, padding: "11px 13px", whiteSpace: "pre-wrap" }}>{plain}</div>;

  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:; base-uri 'none'; form-action 'none'"><base target="_blank"><style>body{font:13px/1.55 -apple-system,system-ui,sans-serif;color:#1d241f;margin:10px;overflow-wrap:break-word}img{max-width:100%;height:auto}a{color:#006747}</style></head><body>${body.data.html}</body></html>`;
  return (
    <div style={{ ...BUBBLE, padding: 0, overflow: "hidden" }}>
      {/* allow-popups-to-escape-sandbox: a link's target=_blank opens the (same-origin,
          Access-gated) interstitial WITHOUT inheriting this frame's sandbox, so its own
          "Continue" link works. Still no allow-scripts / allow-same-origin /
          allow-top-navigation — the frame itself can't script or navigate the parent. */}
      <iframe title="Email content" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={srcDoc} style={{ width: "100%", height: 460, border: 0, display: "block", background: "#fff" }} />
      {body.data.blockedRemoteImages ? (
        <div style={{ fontSize: 11, color: "#8a8e88", padding: "6px 10px", borderTop: "1px solid #f0efe9", background: "#faf9f6" }}>
          {body.data.blockedRemoteImages} remote image{body.data.blockedRemoteImages === 1 ? "" : "s"} blocked for safety.
        </div>
      ) : null}
    </div>
  );
}

type MsgRisk = NonNullable<TicketDetail["thread"][number]["risk"]>;
function RiskBanner({ risk }: { risk: MsgRisk }) {
  const s = RISK_STYLE[risk.level] ?? RISK_STYLE.medium!;
  const ato = risk.reasons.some((r) => r.code === "TRUSTED_SENDER_RISKY_CONTENT");
  const hard = risk.level === "high" || risk.level === "critical";
  // Our own deterministic reasons (trusted copy) vs AI reasons (attacker-influenced
  // free-form model text) — kept separate so the AI output is never presented as a
  // trusted instruction to a junior agent.
  const det = risk.reasons.filter((r) => !r.code.startsWith("AI_") && r.code !== "TRUSTED_SENDER_RISKY_CONTENT").slice(0, 4);
  const aiReasons = risk.reasons.filter((r) => r.code.startsWith("AI_")).slice(0, 2);
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: "9px 12px", marginBottom: 6, fontSize: 12, color: s.fg }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
        <Icon name={s.icon} size={14} color={s.fg} />{s.label}
      </div>
      {ato && <div style={{ marginTop: 3, fontWeight: 600 }}>This is a known sender, but this message is unusual for them — a possible account takeover. Don’t assume it’s safe because you recognize the address.</div>}
      {det.length > 0 && (
        <ul style={{ margin: "5px 0 0", paddingLeft: 18, lineHeight: 1.5 }}>
          {det.map((r, i) => <li key={i}>{r.detail}</li>)}
        </ul>
      )}
      {/* AI output is labeled as unverified and visually set apart — it's context,
          NOT an instruction (the actionable instruction below is our own copy). */}
      {(aiReasons.length > 0 || risk.advice) && (
        <div style={{ marginTop: 6, padding: "6px 8px", borderRadius: 6, background: "rgba(0,0,0,.04)", fontSize: 11.5, opacity: 0.92 }}>
          <span style={{ fontWeight: 700, opacity: 0.7 }}>AI assessment (may be wrong — verify independently):</span>{" "}
          {aiReasons.map((r) => r.detail).concat(risk.advice ? [risk.advice] : []).join(" · ")}
        </div>
      )}
      {hard && (
        <div style={{ marginTop: 6, fontWeight: 700 }}>
          Don’t click links, open attachments, or enter any password. Confirm with the sender on a known phone number before doing anything — and report it if you’re unsure.
        </div>
      )}
    </div>
  );
}

export function Inbox() {
  const { client } = useScope();
  // Deep link from other screens (e.g. "Needs action") — /inbox?ticket=<id>.
  // The open ticket is the PATH param (/tickets/<id>); the queue filter is ?view=.
  // Both are the single source of truth — no local selection state to drift out of
  // sync with the URL. On /inbox there's no param, so `activeId` falls back to the
  // first row (queue landing).
  const params = useParams({ strict: false }) as { ticketId?: string };
  const search = useSearch({ strict: false }) as { view?: string; ticket?: string };
  const navigate = useNavigate();
  const view: View = VIEWS.includes(search.view as View) ? (search.view as View) : "all";
  const setView = (v: View) => navigate({ to: "/inbox", search: { view: v === "all" ? undefined : v } });
  // Open a ticket → /tickets/<id> (shareable path), preserving the active filter.
  const openTicket = (id: string) => navigate({ to: "/tickets/$ticketId", params: { ticketId: id }, search: { view: view === "all" ? undefined : view } });

  const list = useQuery({
    queryKey: ["tickets", client, view],
    queryFn: () => api.tickets({ client: client ?? undefined, view }),
    // Keep the current queue visible while a tab/scope switch refetches, instead
    // of blanking to a full-screen spinner on every key change.
    placeholderData: keepPreviousData,
  });

  const items = list.data?.items ?? [];
  // Don't fall back to items[0] while the queue is still showing the PREVIOUS
  // view's placeholder data — that would bind the detail pane (and its destructive
  // Spam/Delete/Restore controls) to a ticket from the tab you just left.
  // The path is the ONLY source of the open ticket — never auto-bind an off-URL
  // ticket to the actionable detail pane (its Spam/Delete/reply would act on a
  // ticket the address bar doesn't name). On the /inbox landing we redirect to the
  // first ticket so the URL always matches what's shown.
  const activeId = params.ticketId ?? null;
  const firstId = list.isPlaceholderData ? null : items[0]?.id ?? null;
  const viewParam = view === "all" ? undefined : view;
  useEffect(() => {
    // Legacy /inbox?ticket=<id> (bookmarks, history, old deep-links) → canonical path.
    if (search.ticket) {
      navigate({ to: "/tickets/$ticketId", params: { ticketId: search.ticket }, search: { view: viewParam }, replace: true });
      return;
    }
    // /inbox landing → open the first ticket so the URL names what's on screen.
    if (!params.ticketId && firstId) {
      navigate({ to: "/tickets/$ticketId", params: { ticketId: firstId }, search: { view: viewParam }, replace: true });
    }
  }, [search.ticket, params.ticketId, firstId, viewParam, navigate]);

  const detail = useQuery({
    queryKey: ["ticket", activeId],
    queryFn: () => api.ticket(activeId!),
    enabled: !!activeId,
    // NOT keepPreviousData here: the detail pane drives reply/solve/escalate, so
    // showing the previous ticket under a new selection would let an action fire
    // against the wrong ticket. A selection change shows a loader instead.
  });

  // Resolve mailbox ids to their labels so the queue chips name their mailbox
  // instead of every chip reading a bare "Mailbox". Shares the ["mailboxes"]
  // cache with the Mailboxes/Team screens, so it's usually already warm.
  const mailboxMeta = useQuery({ queryKey: ["mailboxes"], queryFn: api.mailboxes });
  const mailboxLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const mb of mailboxMeta.data?.mailboxes ?? []) m.set(mb.id, mb.label);
    return m;
  }, [mailboxMeta.data]);

  const mailboxChips = useMemo(() => {
    const set = new Map<string, number>();
    for (const t of items) if (t.mailboxId) set.set(t.mailboxId, (set.get(t.mailboxId) ?? 0) + 1);
    return [...set.entries()];
  }, [items]);

  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorNote error={list.error} />;

  return (
    <div style={{ height: "100%", minWidth: 1040, display: "grid", gridTemplateColumns: "minmax(260px,320px) minmax(380px,1fr) minmax(240px,300px)", overflow: "hidden" }}>
      {/* QUEUE */}
      <div style={{ borderRight: "1px solid #ececec", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Inbox</h1>
              <div style={{ fontSize: 11, color: "#8a8e88", marginTop: 1 }}>Calls, chats, email, texts · in order received</div>
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#006747", background: "#e8f1ec", borderRadius: 999, padding: "3px 9px", fontFamily: "'Space Mono',monospace" }}>{list.data?.openCount ?? 0} open</span>
          </div>
          {mailboxChips.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
              {mailboxChips.map(([id, n]) => {
                const label = mailboxLabel.get(id) ?? "Mailbox";
                return (
                  <span key={id} title={`${n} ticket${n === 1 ? "" : "s"} from ${label} in this view`} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 600, borderRadius: 999, padding: "3px 8px", border: "1px solid #e7e5df", background: "#f6f5f1", color: "#4a504a", whiteSpace: "nowrap" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#006747" }} />
                    {label} <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 9.5, opacity: 0.75 }}>{n}</span>
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginBottom: 9 }}>
            {VIEWS.map((v) => (
              <button key={v} onClick={() => setView(v)} style={{ fontSize: 11.5, fontWeight: 600, border: 0, borderBottom: `2px solid ${view === v ? "#006747" : "transparent"}`, background: "none", color: view === v ? "#006747" : "#8a8e88", padding: "4px 6px", cursor: "pointer" }}>
                {v === "all" ? "All" : v === "mine" ? "Mine" : v === "risk" ? "SLA at risk" : v === "flagged" ? "Flagged" : v === "spam" ? "Spam" : "Deleted"}
              </button>
            ))}
          </div>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {items.map((r) => {
            const active = r.id === activeId;
            const st = STATUS_TONE[r.status] ?? "neutral";
            const slaTone = r.slaTone ? TONE[r.slaTone] : null;
            return (
              <div key={r.id} onClick={() => openTicket(r.id)} style={{ display: "flex", gap: 10, padding: "12px 14px 12px 12px", borderBottom: "1px solid #f4f3ee", cursor: "pointer", background: active ? "#f9fbfa" : "#fff", borderLeft: `3px solid ${active ? "#006747" : "transparent"}` }}>
                <span style={{ position: "relative", width: 32, height: 32, borderRadius: "50%", flex: "none", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11.5 }}>
                  {r.requesterInitials}
                  {r.live && <span style={{ position: "absolute", right: -1, bottom: -1, width: 9, height: 9, borderRadius: "50%", background: "#1f8a5b", border: "2px solid #fff" }} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: PRIORITY_DOT[r.priority], flex: "none" }} />
                    <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{r.subject}</span>
                    <span style={{ fontSize: 10.5, fontFamily: "'Space Mono',monospace", color: r.live ? "#c0612f" : "#8a8e88", flex: "none" }}>{waited(r.waitedMinutes)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.requester} · {r.clientName}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#8a8e88" }}>
                      <Icon name={CHANNEL_ICON[r.channel] ?? "circle"} size={11} color="#8a8e88" />
                      {CHANNEL_LABEL[r.channel] ?? r.channel}
                    </span>
                    {r.sla && slaTone && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, whiteSpace: "nowrap", flex: "none", background: slaTone.bg, color: slaTone.fg }}>{r.sla}</span>
                    )}
                    {r.source === "mls" && (
                      <span title="From recursive-mls (read-only)" style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".5px", padding: "2px 5px", borderRadius: 3, flex: "none", background: "#ede8f5", color: "#7a5aa8" }}>MLS</span>
                    )}
                    {r.tags.includes("Unknown sender") && (
                      <span title="Not in the roster — unrecognized sender" style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".3px", padding: "2px 6px", borderRadius: 3, flex: "none", background: "#fbe4e2", color: "#b3261e" }}>⚠ UNKNOWN</span>
                    )}
                    {(r.riskLevel === "medium" || r.riskLevel === "high" || r.riskLevel === "critical") && (
                      <span title="Flagged by threat detection" style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".3px", padding: "2px 6px", borderRadius: 3, flex: "none", background: r.riskLevel === "medium" ? "#fbf1dd" : "#fbe4e2", color: r.riskLevel === "medium" ? "#8a5f14" : "#b3261e" }}>
                        {r.riskLevel === "critical" ? "🛑 CRITICAL" : r.riskLevel === "high" ? "⚠ PHISHING" : "⚠ CAUTION"}
                      </span>
                    )}
                    {r.tags.includes("Integration") && (
                      <span title="Automated integration source" style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".3px", padding: "2px 6px", borderRadius: 3, flex: "none", background: "#e8ebf0", color: "#4a5675" }}>INTEGRATION</span>
                    )}
                    <div style={{ flex: 1 }} />
                    <span style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: TONE[st].bg, color: TONE[st].fg }}>{r.status}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div style={{ padding: 20, fontSize: 13, color: "#8a8e88" }}>No tickets in this view.</div>}
        </div>
      </div>

      {/* CONVERSATION */}
      <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: "#f6f5f1" }}>
        {activeId && detail.isLoading && <Loading />}
        {activeId && detail.isError && (
          <div style={{ padding: 28, color: "#8a8e88", fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "#b23b3b", marginBottom: 4 }}>Ticket unavailable</div>
            This ticket doesn’t exist or isn’t in one of your mailboxes.{" "}
            <button onClick={() => setView(view)} style={{ background: "none", border: 0, color: "#006747", fontWeight: 600, cursor: "pointer", padding: 0 }}>Back to the queue</button>
          </div>
        )}
        {activeId && detail.data && <Conversation key={detail.data.id} t={detail.data} />}
        {/* No ticket in the URL: either the queue is empty, or we're about to
            redirect to the first ticket (firstId set) — show a quiet placeholder. */}
        {!activeId && <div style={{ padding: 28, color: "#8a8e88", fontSize: 13 }}>{firstId ? "Opening…" : "No tickets in this view."}</div>}
      </div>

      {/* CONTEXT RAIL */}
      <div style={{ borderLeft: "1px solid #ececec", background: "#fff", overflowY: "auto" }}>
        {/* key by ticket id so per-ticket rail state (e.g. the log-time form
            draft) resets on selection change and can't be recorded elsewhere. */}
        {detail.data && <ContextRail key={detail.data.id} t={detail.data} />}
      </div>
    </div>
  );
}

function Conversation({ t }: { t: TicketDetail }) {
  const [draft, setDraft] = useState("");
  const [drafting, setDrafting] = useState(false);
  const st = STATUS_TONE[t.status] ?? "neutral";
  const slaTone = t.slaTone ? TONE[t.slaTone] : null;
  const [showOriginal, setShowOriginal] = useState(false);
  const queryClient = useQueryClient();

  async function draftWithAI() {
    setDrafting(true);
    try {
      const res = await api.draft(t.id);
      setDraft(res.draft);
    } catch {
      // Leave any existing draft in place on failure — don't wipe the composer.
    } finally {
      setDrafting(false);
    }
  }

  const sendingRef = useRef(false);
  const reply = useMutation({
    mutationFn: (text: string) => api.reply(t.id, text),
    onSuccess: async (data) => {
      // The reply is recorded either way (as sent OR failed), so always refresh the
      // thread + queue to surface the new message and its delivery badge. post()
      // only throws on non-2xx; an app-level {ok:false} (send failed) still resolves
      // — keep the draft in that case so the agent can resend.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ticket", t.id] }),
        queryClient.invalidateQueries({ queryKey: ["tickets"] }),
      ]);
      if (data.ok) setDraft("");
    },
  });

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["ticket", t.id] }),
      queryClient.invalidateQueries({ queryKey: ["tickets"] }),
    ]);
  }
  const [actionError, setActionError] = useState<string | null>(null);
  const actingRef = useRef(false); // synchronous guard (acting only updates next tick)
  const solve = useMutation({
    mutationFn: () => api.setStatus(t.id, "solved"),
    onSuccess: (d) => d.ok && refresh(),
    onError: (e) => setActionError(e instanceof Error ? e.message : "Solve failed"),
    onSettled: () => { actingRef.current = false; },
  });
  const escalate = useMutation({
    mutationFn: () => api.escalate(t.id),
    onSuccess: (d) => d.ok && refresh(),
    onError: (e) => setActionError(e instanceof Error ? e.message : "Escalate failed"),
    onSettled: () => { actingRef.current = false; },
  });
  const setStatus = useMutation({
    mutationFn: (status: string) => api.setStatus(t.id, status),
    onSuccess: (d) => d.ok && refresh(),
    onError: (e) => setActionError(e instanceof Error ? e.message : "Couldn't update"),
    onSettled: () => { actingRef.current = false; },
  });
  const acting = solve.isPending || escalate.isPending || setStatus.isPending;
  const isHidden = t.status === "spam" || t.status === "deleted";
  function runAction(m: { mutate: () => void }) {
    if (acting || actingRef.current) return;
    actingRef.current = true;
    setActionError(null);
    m.mutate();
  }
  function runStatus(status: string) {
    if (acting || actingRef.current) return;
    actingRef.current = true;
    setActionError(null);
    setStatus.mutate(status);
  }

  // recursive-mls tickets are read-only in this app until Access is configured.
  // MLS-internal tickets and imported/mirror archival records are read-only: the
  // action endpoints (status/escalate) 409 on them server-side, so disable the buttons.
  const readOnly = t.source === "mls" || t.archival === true;
  const canSend = draft.trim().length > 0 && !drafting && !reply.isPending && !readOnly && !isHidden;
  function send() {
    // sendingRef is a synchronous guard: canSend/disabled only update on the
    // next render, so a double-click or Cmd+Enter repeat in one tick could
    // otherwise fire two POSTs → duplicate outbound emails.
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    reply.mutate(draft.trim(), { onSettled: () => { sendingRef.current = false; } });
  }

  return (
    <>
      <div style={{ background: "#fff", borderBottom: "1px solid #ececec", padding: "13px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, color: "#8a8e88" }}>{t.ref}</span>
          {t.archival && t.hubspotId ? <span title="Original HubSpot ticket id" style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: "#8a8e88", opacity: 0.85 }}>· HS #{t.hubspotId}</span> : null}
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: TONE[st].bg, color: TONE[st].fg }}>{t.status}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "#8a8e88" }}>
            <Icon name={CHANNEL_ICON[t.channel] ?? "circle"} size={13} color="#8a8e88" />via {CHANNEL_LABEL[t.channel] ?? t.channel}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "#16243a", background: "#e8ebf0", borderRadius: 4, padding: "3px 8px" }}>
            <Icon name="building-2" size={12} color="#16243a" />{t.clientName}
          </span>
          {t.sla && slaTone && <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 7px", borderRadius: 3, background: slaTone.bg, color: slaTone.fg }}>SLA · {t.sla}</span>}
          <span title="Support tier holding this ticket" style={{ fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 3, background: "#f0efe9", color: "#6b706a", fontFamily: "'Space Mono',monospace" }}>T{t.tier}</span>
          <div style={{ flex: 1 }} />
          {actionError && <span style={{ fontSize: 11, color: "#b23b3b" }}>{actionError}</span>}
          {readOnly && <span title={t.archival ? "Imported record — archival, read-only" : "From recursive-mls"} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".5px", padding: "3px 8px", borderRadius: 999, background: "#ede8f5", color: "#7a5aa8" }}>{t.archival ? "Archived · read-only" : "MLS · read-only"}</span>}
          {isHidden ? (
            <button onClick={() => runStatus("open")} disabled={acting || readOnly} title="Restore this ticket to the inbox" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "6px 10px", cursor: acting || readOnly ? "default" : "pointer", opacity: acting || readOnly ? 0.5 : 1 }}>
              <Icon name="refresh-cw" size={13} color="#006747" />{setStatus.isPending ? "Restoring…" : "Restore"}
            </button>
          ) : (
            <>
              <button onClick={() => runStatus("spam")} disabled={acting || readOnly} title="Mark as spam — hidden from the inbox (recoverable in the Spam view)" style={{ fontSize: 12, fontWeight: 600, color: "#9a6a12", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "6px 10px", cursor: acting || readOnly ? "default" : "pointer", opacity: acting || readOnly ? 0.5 : 1 }}>Spam</button>
              <button onClick={() => runStatus("deleted")} disabled={acting || readOnly} title="Delete — hidden from the inbox (recoverable in the Deleted view)" style={{ fontSize: 12, fontWeight: 600, color: "#b23b3b", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "6px 10px", cursor: acting || readOnly ? "default" : "pointer", opacity: acting || readOnly ? 0.5 : 1 }}>Delete</button>
              <button onClick={() => runAction(escalate)} disabled={acting || readOnly} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "6px 10px", cursor: acting || readOnly ? "default" : "pointer", opacity: acting || readOnly ? 0.5 : 1 }}>
                <Icon name="git-branch" size={13} color="#006747" />{escalate.isPending ? "Escalating…" : "Escalate"}
              </button>
              <button onClick={() => t.status !== "solved" && runAction(solve)} disabled={acting || readOnly || t.status === "solved"} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 7, padding: "7px 11px", cursor: acting || readOnly || t.status === "solved" ? "default" : "pointer", opacity: acting || readOnly || t.status === "solved" ? 0.6 : 1 }}>
                <Icon name="check-circle" size={13} color="#fff" />{solve.isPending ? "Solving…" : t.status === "solved" ? "Solved" : "Solve"}
              </button>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 9 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, flex: 1, letterSpacing: "-.3px" }}>{t.subject}</h2>
          {t.tags.map((tg) => {
            const kind = tg === "Unknown sender" ? "unknown" : tg === "Integration" ? "integration" : "normal";
            const c = kind === "unknown" ? { background: "#fbe4e2", color: "#b3261e" } : kind === "integration" ? { background: "#e8ebf0", color: "#4a5675" } : { background: "#f0efe9", color: "#4a504a" };
            return <span key={tg} style={{ fontSize: 11, fontWeight: kind === "normal" ? 400 : 700, borderRadius: 4, padding: "3px 8px", ...c }}>{tg}</span>;
          })}
        </div>
        {t.tags.includes("Unknown sender") && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, background: "#fbe9e7", border: "1px solid #f3c9c4", borderRadius: 6, padding: "9px 12px", color: "#b3261e", fontSize: 12.5, fontWeight: 600 }}>
            <Icon name="alert-triangle" size={16} color="#b3261e" />
            Unrecognized sender — not in the client roster. Verify identity before sharing account details or taking any account action.
          </div>
        )}
        {t.linked && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, background: "#f6f5f1", border: "1px solid #e7e5df", borderRadius: 6, padding: "8px 12px" }}>
            <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase", color: "#8a8e88", flex: "none" }}>Related to</span>
            <span style={{ width: 26, height: 26, borderRadius: 5, background: "#e8f4ee", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
              <Icon name={String((t.linked as { icon?: string }).icon ?? "circle")} size={13} color="#006747" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{String((t.linked as { label?: string }).label ?? "")}</div>
              <div style={{ fontSize: 11, color: "#8a8e88" }}>{String((t.linked as { sub?: string }).sub ?? "")}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ alignSelf: "center", display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "#8a8e88", background: "#fff", border: "1px solid #ececec", borderRadius: 999, padding: "5px 13px" }}>
          <Icon name={CHANNEL_ICON[t.channel] ?? "circle"} size={12} color="#006747" />
          Received by {(CHANNEL_LABEL[t.channel] ?? t.channel).toLowerCase()}
        </div>
        {t.thread.map((m, i) => {
          const mine = m.from === "me";
          const sys = m.from === "sys";
          // A rich inbound email renders full-width (not in a narrow chat bubble).
          const richEmail = !mine && m.channel === "email" && !!m.id && (!m.original || showOriginal);
          if (sys) {
            return (
              <div key={i} style={{ alignSelf: "center", fontSize: 11.5, color: "#8a8e88", background: "#f0efe9", borderRadius: 999, padding: "4px 12px" }}>{m.body}</div>
            );
          }
          return (
            <div key={i} style={{ display: "flex", gap: 10, flexDirection: mine ? "row-reverse" : "row" }}>
              <span style={{ width: 32, height: 32, borderRadius: "50%", flex: "none", background: mine ? "#006747" : "#e7e1d4", color: mine ? "#fff" : "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 11.5 }}>
                {mine ? "DL" : (m.authorName ?? "?").split(" ").map((w) => w[0]).join("").slice(0, 2)}
              </span>
              <div style={{ minWidth: 0, ...(richEmail ? { flex: 1 } : { maxWidth: "76%" }) }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexDirection: mine ? "row-reverse" : "row" }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{mine ? "Devon Lang" : m.authorName}</span>
                  <span style={{ fontSize: 10.5, color: "#bcbfba" }}>{m.at}</span>
                </div>
                {m.risk && <RiskBanner risk={m.risk} />}
                {/* Rich HTML for inbound email — including translated messages when the
                    reader has toggled to the original (the HTML IS the original); a
                    translation-on view falls back to the plain translated text. */}
                {richEmail && m.id ? (
                  <EmailBody ticketId={t.id} messageId={m.id} level={m.risk?.level} plain={showOriginal && m.original ? m.original : m.body} />
                ) : (
                  <div style={{ fontSize: 13, background: mine ? "#e8f4ee" : "#fff", border: `1px solid ${mine ? "#d6e7de" : "#ececec"}`, color: "#1d241f", borderRadius: 8, padding: "11px 13px", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                    {showOriginal && m.original ? m.original : m.body}
                  </div>
                )}
                {m.original && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 11, color: "#8a8e88" }}>
                    <Icon name="languages" size={12} color="#006747" />
                    Translated from {m.lang}
                    <a href="#" onClick={(e) => { e.preventDefault(); setShowOriginal((v) => !v); }} style={{ fontWeight: 600 }}>{showOriginal ? "Show translation" : "Show original"}</a>
                  </div>
                )}
                {mine && m.delivery && (
                  <div
                    title={m.deliveryDetail ?? undefined}
                    style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, justifyContent: "flex-end", fontSize: 10.5, fontWeight: 600, color: (DELIVERY_BADGE[m.delivery] ?? { color: "#8a8e88" }).color }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: (DELIVERY_BADGE[m.delivery] ?? { color: "#8a8e88" }).color }} />
                    {(DELIVERY_BADGE[m.delivery] ?? { label: m.delivery }).label}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      {isHidden && (
        <div style={{ background: "#fbf6e9", borderTop: "1px solid #efe2c0", color: "#7a5f14", fontSize: 12, padding: "8px 16px" }}>
          This ticket is {t.status}. Restore it to reply.
        </div>
      )}
      <div style={{ background: "#fff", borderTop: "1px solid #ececec", padding: "10px 16px 14px" }}>
        <div style={{ border: "1px solid #e2e0d8", borderRadius: 8, overflow: "hidden" }}>
          <textarea
            rows={3}
            value={drafting ? "Drafting with AI…" : draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                send();
              }
            }}
            readOnly={drafting || reply.isPending || isHidden}
            placeholder={isHidden ? `Restore this ticket to reply` : `Reply to ${t.requester}…`}
            style={{ width: "100%", border: 0, outline: 0, resize: "none", fontSize: 13.5, color: drafting || isHidden ? "#8a8e88" : "#1d241f", padding: "11px 12px", lineHeight: 1.55, background: "transparent", display: "block", cursor: isHidden ? "not-allowed" : "text" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderTop: "1px solid #f0efe9", background: "rgba(0,0,0,.015)" }}>
            {reply.isError ? (
              <span style={{ fontSize: 11.5, color: "#b23b3b" }}>
                Couldn't send — {reply.error instanceof Error ? reply.error.message : "try again"}
              </span>
            ) : reply.data && !reply.data.sent.ok ? (
              <span style={{ fontSize: 11.5, color: "#b23b3b" }}>
                Not delivered — saved on {reply.data.ref} as failed{reply.data.sent.error ? ` (${reply.data.sent.error})` : ""}. The ticket stays open; fix the address and resend.
              </span>
            ) : (
              <span style={{ fontSize: 11, color: "#bcbfba" }}>⌘/Ctrl + Enter to send · reply goes out from the ticket's mailbox</span>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={draftWithAI} disabled={drafting || reply.isPending || isHidden} title={isHidden ? "Restore this ticket to reply" : undefined} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #d6e7de", borderRadius: 7, padding: "6px 10px", cursor: drafting || isHidden ? "default" : "pointer", opacity: drafting || reply.isPending || isHidden ? 0.6 : 1 }}>
              <Icon name="sparkles" size={13} color="#006747" />{drafting ? "Drafting…" : "Draft with AI"}
            </button>
            <button onClick={send} disabled={!canSend} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 7, padding: "7px 12px", cursor: canSend ? "pointer" : "default", opacity: canSend ? 1 : 0.55 }}>
              <Icon name="mail" size={13} color="#fff" />{reply.isPending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Shared section-label style — small uppercase eyebrow used across the rail.
const RAIL_LABEL: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88" };
// Open the docked softphone on a given tab (decoupled window event — see Softphone).
const openPhone = (tab: "dial" | "text" | "verify") => window.dispatchEvent(new CustomEvent("softphone:open", { detail: { tab } }));

function ContextRail({ t }: { t: TicketDetail }) {
  const navigate = useNavigate();
  // Call/text/verify open the docked softphone, which Shell only mounts (listener
  // and all) when the telephony flag is on. So gate the controls on the same flag —
  // otherwise they'd be dead buttons in the default/telephony-off config. The
  // ["flags"] query is already populated by Shell, so this just reads the cache.
  const flags = useQuery({ queryKey: ["flags"], queryFn: api.flags });
  const canPhone = !!flags.data?.telephony;
  const sentTone = t.sentimentTone ? TONE[t.sentimentTone] : TONE.neutral;
  // Reuse the person endpoint's resolution (verified RealtyID identity, member
  // facts, support history) instead of duplicating Displet/RealtyID reads here.
  // Only fires when the sender resolved to a known individual.
  const person = useQuery({
    queryKey: ["person", t.requesterId],
    queryFn: () => api.person(t.requesterId!),
    enabled: !!t.requesterId,
    staleTime: 60_000,
  });
  const p = person.data;
  const rid = p?.dossier?.realtyId ?? null;
  const dir = p?.directory ?? null;
  const memberFacts: { k: string; v: string }[] = [];
  if (dir?.license && dir.license !== "—") memberFacts.push({ k: "License", v: dir.license });
  if (dir?.office && dir.office !== "—") memberFacts.push({ k: "Office", v: dir.office });
  if (p?.role) memberFacts.push({ k: "Role", v: p.role });
  const memberTags = dir?.tags ?? [];
  const hasMember = !!p && (!!p.memberKey || memberFacts.length > 0 || memberTags.length > 0);
  // The requester's other tickets (exclude the one we're already looking at).
  const recent = (p?.tickets ?? []).filter((r) => r.id !== t.id).slice(0, 6);
  const priColor = PRIORITY_DOT[t.priority as keyof typeof PRIORITY_DOT] ?? "#8a8e88";

  return (
    <>
      <div style={{ padding: "18px 16px 14px", borderBottom: "1px solid #f0efe9", display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ width: 44, height: 44, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 15, flex: "none" }}>{t.requesterInitials}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{t.requester}</div>
          <div style={{ fontSize: 12, color: "#8a8e88", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.role}</div>
        </div>
        {canPhone && (
          <button title="Call" onClick={() => openPhone("dial")} style={{ width: 32, height: 32, borderRadius: 7, background: "#fff", border: "1px solid #e2e0d8", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <Icon name="phone" size={14} color="#006747" />
          </button>
        )}
      </div>

      {t.summary && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <Icon name="sparkles" size={12} color="#006747" />
            <span style={{ ...RAIL_LABEL, flex: 1 }}>AI summary</span>
            {t.sentiment && <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: sentTone.bg, color: sentTone.fg }}>{t.sentiment}</span>}
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#4a504a" }}>{t.summary}</div>
        </div>
      )}

      {/* License record — state-license data mirrored from RealtyID. This is roster
          data, NOT contact-identity proof: resolveRealtyId returns ial/proofedAt
          null, and requesterKey is only an email-address match (no DMARC guarantee),
          so a spoofed/compromised known mailbox must not be labelled "verified". The
          shield lives on the Verify (OTP) action instead. Shown only when the sender
          resolved to a person with a RealtyID on file. */}
      {rid && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
            <Icon name="scroll-text" size={13} color="#006747" />
            <span style={{ ...RAIL_LABEL, flex: 1 }}>License record · RealtyID</span>
            {rid.ial != null && <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#e8ebf0", color: "#16243a" }}>IAL{rid.ial}</span>}
          </div>
          <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 700, color: "#006747", marginBottom: 8 }}>{rid.rid}</div>
          {rid.licenses.length === 0 && <div style={{ fontSize: 12, color: "#8a8e88" }}>No licenses on file.</div>}
          {rid.licenses.map((l, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderTop: "1px solid #f4f3ee" }}>
              <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 700, flex: "none", width: 26 }}>{l.state}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{l.type} · {l.number}</div>
                <div style={{ fontSize: 11, fontWeight: 600, marginTop: 1, color: l.eligible == null ? "#8a8e88" : l.eligible ? "#1f8a5b" : "#b3261e" }}>
                  {l.eligible == null ? "Eligibility not available" : l.eligible ? "Eligible to practice" : "Not eligible to practice"}
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap", background: String(l.status ?? "").toLowerCase() === "active" ? "#e8f4ee" : "#fbe9e7", color: String(l.status ?? "").toLowerCase() === "active" ? "#1f8a5b" : "#b3261e" }}>{l.status ?? "unknown"}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#8a8e88", lineHeight: 1.5, marginTop: 8 }}>{rid.disciplineStatement}</div>
        </div>
      )}

      {/* Member record in the client's system (Displet) — distinct from RealtyID. */}
      {hasMember && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
            <span style={{ ...RAIL_LABEL, flex: 1 }}>Member · in {p?.clientName ?? t.clientName}</span>
            {p?.memberKey && <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 10.5, color: "#8a8e88" }}>{p.memberKey}</span>}
          </div>
          {memberFacts.map((f) => (
            <div key={f.k} style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 7 }}>
              <span style={{ fontSize: 12, color: "#8a8e88", flex: "none" }}>{f.k}</span>
              <span style={{ fontSize: 12, fontWeight: 600, textAlign: "right", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{f.v}</span>
            </div>
          ))}
          {memberTags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
              {memberTags.map((tag) => (
                <span key={tag} style={{ fontSize: 10.5, background: "#f0efe9", borderRadius: 4, padding: "2px 7px" }}>{tag}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Client (tenant) facts + a jump to the Client 360. */}
      {t.clientFacts && t.clientFacts.length > 0 && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ ...RAIL_LABEL, marginBottom: 9 }}>Client · {t.clientName}</div>
          {t.clientFacts.map((f) => (
            <div key={f.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: "#8a8e88" }}>{f.k}</span>
              <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>{f.v}</span>
            </div>
          ))}
          {t.clientId && (
            <button onClick={() => navigate({ to: "/clients/$clientId", params: { clientId: t.clientId! } })} style={{ fontSize: 12, fontWeight: 600, color: "#006747", background: "transparent", border: 0, padding: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
              Client 360 <Icon name="arrow-up-right" size={12} color="#006747" />
            </button>
          )}
        </div>
      )}

      {/* Escalation path — current tier + the ordered offer history. */}
      {t.escalation && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 9 }}>
            <span style={{ ...RAIL_LABEL, flex: 1 }}>Escalation path</span>
            <span style={{ fontSize: 11, color: "#8a8e88" }}>Tier {t.escalation.tier}{t.escalation.tierRule ? ` · ${t.escalation.tierRule}` : ""}</span>
          </div>
          {t.escalation.path.length === 0 && <div style={{ fontSize: 12, color: "#8a8e88" }}>Not yet accepted — offering on shift.</div>}
          {t.escalation.path.map((step, i) => {
            // outcome_tone is a Drizzle TS-only enum (no SQLite CHECK), so a
            // legacy/junk value can arrive outside TONE's keys — guard the lookup.
            const tone = (step.tone && TONE[step.tone]) || TONE.neutral;
            return (
              <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", fontSize: 12 }}>
                <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 10.5, color: "#8a8e88", width: 52, flex: "none" }}>{step.at ? String(step.at).slice(0, 10) : "—"}</span>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>Tier {step.tier}</span> <span style={{ color: "#4a504a" }}>· {step.who}</span>
                  {step.note && <div style={{ fontSize: 11.5, color: tone.fg }}>{step.note}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Requester contact — email / phone / priority + call, text & verify. */}
      {(t.email || t.phone) && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 9 }}>
            <span style={{ ...RAIL_LABEL, flex: 1 }}>Requester</span>
            {canPhone && (
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={() => openPhone("dial")} title="Call" style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e2e0d8", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="phone" size={13} color="#006747" /></button>
                <button onClick={() => openPhone("text")} title="Text" style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e2e0d8", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="message-square" size={13} color="#006747" /></button>
                <button onClick={() => openPhone("verify")} title="Verify identity by text code" style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e2e0d8", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="shield-check" size={13} color="#006747" /></button>
              </div>
            )}
          </div>
          {t.email && <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8 }}><span style={{ fontSize: 12, color: "#8a8e88" }}>Email</span><span style={{ fontSize: 12, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{t.email}</span></div>}
          {t.phone && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ fontSize: 12, color: "#8a8e88" }}>Phone</span><span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 600 }}>{t.phone}</span></div>}
          <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 12, color: "#8a8e88" }}>Priority</span><span style={{ fontSize: 12, fontWeight: 600, textTransform: "capitalize", color: priColor }}>{t.priority}</span></div>
        </div>
      )}

      {t.survey && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <span style={RAIL_LABEL}>Linked in Survey</span>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, fontWeight: 700, color: "#006747" }}>{String((t.survey as { key?: string }).key ?? "")}</span>
            <span style={{ fontSize: 11.5, color: "#4a504a" }}>{String((t.survey as { label?: string }).label ?? "")}</span>
          </div>
        </div>
      )}

      {/* Requester's other tickets — quick jumps to their support history. */}
      {recent.length > 0 && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
          <div style={{ ...RAIL_LABEL, marginBottom: 9 }}>Recent tickets</div>
          {recent.map((r) => {
            const tone = TONE[STATUS_TONE[r.status] ?? "neutral"];
            return (
              <div key={r.id} onClick={() => navigate({ to: "/tickets/$ticketId", params: { ticketId: r.id } })} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", cursor: "pointer" }}>
                <Icon name="ticket" size={13} color="#8a8e88" />
                <span style={{ flex: 1, fontSize: 12.5, color: "#4a504a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.subject}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: tone.bg, color: tone.fg, textTransform: "capitalize" }}>{r.status}</span>
              </div>
            );
          })}
        </div>
      )}

      {t.source !== "mls" && <TimeLogged t={t} />}
    </>
  );
}

// Minutes → a compact "2h 15m" / "45m" label.
function fmtMinutes(m: number): string {
  if (m <= 0) return "0m";
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h ? `${h}h${min ? ` ${min}m` : ""}` : `${min}m`;
}

// §4 log-time overlay — the running time logged against this ticket plus an
// inline form to add an entry. Only rendered for our own D1 tickets.
function TimeLogged({ t }: { t: TicketDetail }) {
  const qc = useQueryClient();
  const time = useQuery({ queryKey: ["ticket-time", t.id], queryFn: () => api.ticketTime(t.id) });
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState("");
  const [billable, setBillable] = useState(false);
  const [note, setNote] = useState("");

  const mins = Math.floor(Number(minutes));
  const valid = Number.isFinite(mins) && mins > 0 && mins <= 24 * 60;

  const log = useMutation({
    mutationFn: () => api.logTime(t.id, mins, billable, note.trim()),
    onSuccess: (d) => {
      if (!d.ok) return;
      setOpen(false);
      setMinutes("");
      setNote("");
      setBillable(false);
      qc.invalidateQueries({ queryKey: ["ticket-time", t.id] });
    },
  });

  // Synchronous guard: log.isPending only flips on the next render, so a
  // double-click in one tick would otherwise fire two mutations → duplicate
  // (billable) entries. Same pattern the reply composer uses (sendingRef).
  const savingRef = useRef(false);
  function save() {
    if (!valid || log.isPending || savingRef.current) return;
    savingRef.current = true;
    log.mutate(undefined, { onSettled: () => { savingRef.current = false; } });
  }

  const total = time.data?.totalMinutes ?? 0;
  const entries = time.data?.entries ?? [];

  return (
    <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0efe9" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon name="clock" size={12} color="#006747" />
        <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88", flex: 1 }}>Time logged</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: "'Space Mono',monospace" }}>{fmtMinutes(total)}</span>
      </div>
      {entries.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 8 }}>
          {entries.map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 11.5, color: "#4a504a" }}>
              <span style={{ fontFamily: "'Space Mono',monospace", color: "#006747", flex: "none" }}>{fmtMinutes(e.minutes)}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {e.who}{e.note ? <span style={{ color: "#8a8e88" }}> · {e.note}</span> : null}
              </span>
              {e.billable && <span title="Billable" style={{ fontSize: 9.5, fontWeight: 700, color: "#00543a", background: "#eef4f1", borderRadius: 3, padding: "1px 5px", flex: "none" }}>BILL</span>}
            </div>
          ))}
        </div>
      )}
      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="Minutes"
              style={{ width: 88, fontSize: 12.5, padding: "6px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }}
            />
            <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#4a504a", cursor: "pointer" }}>
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable
            </label>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Note (optional)"
            style={{ fontSize: 12.5, padding: "6px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={save}
              disabled={!valid || log.isPending}
              style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: valid && !log.isPending ? "#006747" : "#b7c3bd", border: 0, borderRadius: 6, padding: "6px 11px", cursor: valid && !log.isPending ? "pointer" : "default" }}
            >
              {log.isPending ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setOpen(false)} style={{ fontSize: 12, fontWeight: 600, color: "#8a8e88", background: "none", border: 0, cursor: "pointer" }}>Cancel</button>
            {log.isError && <span style={{ fontSize: 11, color: "#b3261e" }}>Couldn't save.</span>}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #cfe0d6", borderRadius: 7, padding: "5px 10px", cursor: "pointer" }}
        >
          <Icon name="plus" size={13} color="#006747" /> Log time
        </button>
      )}
    </div>
  );
}
